<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" hide-details clearable />
            </template>
            <template #default>
              <v-row>
                <v-col>
                  <select-list
                    v-model="query.params.offerType"
                    :disabled="isLoading || hasErrors"
                    :items="getItemsForOption('offerTypes')"
                    label="Offer Type"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col>
                  <select-list
                    v-model="query.params.availableCountries"
                    :disabled="isLoading || hasErrors"
                    :items="getItemsForOption('countries')"
                    label="Available Countries"
                    clearable
                    hide-details
                    multiple
                  />
                </v-col>
                <v-col>
                  <select-list
                    v-model="query.params.published"
                    :disabled="isLoading || hasErrors"
                    :items="publishedOptions"
                    label="Status"
                    clearable
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>
            <template #content>
              <data-table
                :footer-props="{ 'items-per-page-options': perPageOptions }"
                :headers="tableHeaders"
                :items="offers"
                :items-per-page="pagination.perPage"
                :page="pagination.currentPage"
                :route="{ name: 'offers.edit', params: ['slug'] }"
                :server-items-length="pagination.total"
                disable-sort
                @pagination="$_queryHandler_onUpdatePagination"
              >
                <template #item="{ item }">
                  <td>
                    <div>
                      <item-logo :name="item.name" :src="item.logoThumbnail" class="mr-2 ml-0w" />
                      <span>{{ item.name }}</span>
                    </div>
                  </td>
                  <td>{{ formattedOption('offerTypes', item.type) }}</td>
                  <td>
                    <v-rating
                      color="orange"
                      background-color="grey"
                      dense
                      half-increments
                      readonly
                      small
                      :value="formatStarRating(item.starRating)"
                    />
                  </td>
                  <td class="text-right">
                    <published-chip :value="item.published" />
                  </td>
                </template>
              </data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <fab-actions
      :can-create="isPermittedTo('create')"
      :labels="{ export: 'Export Offers' }"
      context="Offer"
      @export="onClickExport"
      @create="showOfferCreateModal = true"
    />

    <offer-create-modal
      v-model="showOfferCreateModal"
      :has-errors="hasErrors"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      @modal-confirm="onModalConfirm"
    />
  </page-loader>
</template>

<script>
// Helpers
import { mapGetters, mapMutations } from 'vuex'
import { exportCSV } from '@/utils/csv'
// Components
import DataTable from '@/components/data-table'
import FabActions from '@/components/fab-actions'
import FilterAccordion from '@/components/filter-accordion'
import ItemLogo from '@/components/item-logo'
import OfferCreateModal from '@/views/offers/components/offer-create-modal'
import PageLoader from '@/components/page-loader'
import PublishedChip from '@/components/chips/published'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'
// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Services
import { offers } from '@/services/offers'

export default {
  name: 'OfferManagement',
  components: {
    DataTable,
    FabActions,
    FilterAccordion,
    ItemLogo,
    OfferCreateModal,
    PageLoader,
    PublishedChip,
    SearchInput,
    SelectList,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data: () => ({
    isLoading: false,
    offers: [],
    perPageOptions: [15, 30, 50, -1],
    pagination: {
      perPage: 30,
    },
    publishedOptions: [
      { key: 'Draft', value: false },
      { key: 'Published', value: true },
    ],
    defaultQuery: {
      offerType: 'crypto',
    },
    queryDefaults: ['offerType'],
    queriesDefined: ['availableCountries', 'offerType', 'published'],
    searchKey: 'name',
    showOfferCreateModal: false,
    tableHeaders: [
      { text: 'Name', value: 'name' },
      { text: 'Offer Type', value: 'type' },
      { text: 'Rating', value: 'rating' },
      { text: 'Status', value: 'status', align: 'right' },
    ],
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors', 'isPageLoading']),
    ...mapGetters('options', ['getItemsForOption', 'hasOptions', 'formattedOption']),
    hasErrors() {
      return !this.hasOptions || this.hasPageErrors
    },
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async onModalConfirm({ name, type }) {
      if (!name || !type) return
      this.isLoading = true

      try {
        const { slug } = await offers.createOffer(type, { default: { name } })
        this.showOfferCreateModal = false
        this.$router.push({ name: 'offers.edit', params: { slug } })
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to create offer',
        })
      }

      this.isLoading = false
    },
    formatStarRating(rating) {
      if (!rating) return 0
      const outOfFive = (rating / 100) * 5
      return Number((Math.round(outOfFive * 2) / 2).toFixed(1))
    },
    async getOffers() {
      this.isLoading = true
      const { perPage, currentPage: page } = this.pagination

      try {
        const { data, pagination: { total } = {} } = await offers.getAllOffers({
          compact: true,
          page,
          perPage: perPage === -1 ? null : perPage,
          filters: this.queryFilters,
        })
        this.offers = data || []
        this.pagination.total = total
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve offers',
        })
      }

      this.setPageLoading(false)
      this.isLoading = false
    },
    async handleQueryUpdate() {
      await this.getOffers()
    },
    async onClickExport() {
      this.isLoading = true

      const { data } = await offers.getAllOffers({ compact: true, filters: this.queryFilters })
      const headers = ['name', 'slug', 'status'].join(',')
      const body = data.map(({ name, published, slug }) => [name, slug, published ? 'published' : 'draft'].join(','))

      const csv = [headers].concat(body).join('\n')

      exportCSV(csv, 'offers-export')

      this.isLoading = false
    },
  },
}
</script>
